<!-- 
  NOTE: this component allows to select a value from an input list
  If the list is filtered, it will check for eventual filters in the assetsCreateEdit store.
 -->
<template lang="pug">
template(v-if="selectFilterFirst")
  div {{ $t("selectFilterFirst", { filter: props.filterName ? props.filterName : filterProperty?.name }) }}
.select-value.flex(v-else)
  el-select(
    v-model="model",
    filterable,
    :loading="loadingState.loading",
    :clearable="props.clearable",
    :disabled="props.disabled",
    :allow-create="allowCreate"
  ) 
    el-option(
      v-for="item in options",
      :key="item.uuid",
      :value="item.name",
      :label="item.name"
    ) 
  el-button(
    v-if="hasSmxModelMgmtRole && !hideEditing",
    text,
    type="primary",
    @click="editInputValues = true"
  ) {{ $t("edit") }}
el-dialog(v-model="editInputValues")
  InputValueDetail(:id="id")
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useTwinStore } from "../../../stores/twinManagement";
import { stateInitial, type LoadState } from "../../../models/state";
import { type ValueListItem } from "../../../services/twinData";
import { useTwinEditCreateStore } from "../../../stores/twinCreateEdit";
import { type Property } from "../../../services/twinManagement";
import { useAuthStore } from "../../../stores/auth";
import InputValueDetail from "../components/InputValueDetail.vue";

const twinStore = useTwinStore();
const twinEditCreateStore = useTwinEditCreateStore();

const props = defineProps<{
  modelValue?: string | null;
  // Which list is to be used can be defined by setting either id, customId or property
  id?: string;
  customId?: string;
  property?: Property;
  disabled?: boolean;
  clearable?: boolean;
  // If set AND the property is filtered by another input value, this value will be used as filter.
  // Otherwise the currently edited twin will be searched for a filter with the correct filterId and its value used as filter
  filterValue?: string;
  // name to be shown if the filterValue is not set. Should be provided if the filterValue is set manually.
  filterName?: string;
  // Remove certain values from the options
  removeValues?: string[];
  // allow ad hoc creation of values
  allowCreate?: boolean;
  // Hide the edit button for admins
  hideEditing?: boolean;
}>();

const emit = defineEmits(["update:modelValue"]);

onMounted(() => {
  twinStore.loadValueLists({ refresh: false });
  loadData();
});

// If customId is used to identify the value list, we have to wait for the value lists to be loaded and then load the values
watch(
  () => twinStore.valueListsLoadingState.loaded,
  (loaded) => {
    if (props.customId) {
      if (loaded) {
        const id = twinStore.valueListsLoadingState.result?.find(
          (vl) => vl.customId === props.customId,
        )?.uuid;
        if (id) {
          twinStore.loadValueListItems(id);
        }
      }
    }
  },
);

const loadingState = ref<LoadState<void>>({ ...stateInitial });

const listMetaData = computed(() => {
  const result = (twinStore.valueListsLoadingState.result ?? []).find(
    (item) => item.uuid === id.value,
  );
  return result;
});

const id = computed(() => {
  if (props.id !== undefined) {
    return props.id;
  }
  if (props.property?.constraint?.settings?.inputValue !== undefined) {
    return props.property.constraint.settings.inputValue;
  }
  if (props.customId !== undefined) {
    return (twinStore.valueListsLoadingState.result ?? []).find(
      (item) => item.customId === props.customId,
    )?.uuid;
  }
  throw Error(
    "ValueListSelect: Either id, property or customId have to be set",
  );
});

const allowCreate = computed(
  () => props.allowCreate || props.property?.constraint?.settings?.allowCreate,
);

const model = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    if (allowCreate.value) {
      checkIfValueExistsAndCreate(value);
    }
    emit("update:modelValue", value);
  },
});

function checkIfValueExistsAndCreate(value: string) {
  // NOTE: when we allow creating values, we want to check if the selected value exists, or if we should create it.
  // since we don't check this in the backend, we do this in the background and accept possible failures, the value can be stored anyways
  if (value && !options.value.some((item) => item.name === value)) {
    twinStore.createValueListItem({
      valueListId: id.value,
      value,
      name: value,
      filterValue: filterValueListItem.value?.uuid,
    });
  }
}

const options = computed(() =>
  isFiltered.value ? filteredResult.value : result.value,
);

// NOTE: since there might be multiple lists, we load the items locally, this also allows simple future search/paging
const result = computed(() => {
  const values =
    twinStore.valueListItemsLoadingState.get(id.value)?.result || [];
  if (props.removeValues) {
    return values.filter((item) => !props.removeValues.includes(item.value));
  }
  return values;
});

// Filtering
const filterId = computed(() => listMetaData.value?.filterId);
const filterPropertyId = computed(
  () => props.property?.constraint?.settings?.inputValueFilterProperty,
);
const filterProperty = computed(() => {
  if (props.filterValue) {
    return null;
  }
  if (twinEditCreateStore.asset === undefined) {
    return null;
  }
  if (twinEditCreateStore.isEditing) {
    // When editing, we search for a property with propertyTemplateUuid being the filterId
    return (twinEditCreateStore.asset?.properties ?? []).find(
      (item) => item.propertyTemplateUuid === filterPropertyId.value,
    );
  } else {
    // When not editing, we search for a property in the template with the filterId
    return (twinEditCreateStore.asset?.properties ?? []).find(
      (item) => item.uuid === filterPropertyId.value,
    );
  }
});

const isFiltered = computed(() => filterId.value != null);

const filterValue = computed(() => {
  if (props.filterValue) {
    return props.filterValue;
  }
  if (filterProperty.value) {
    return twinEditCreateStore.mergedInputs[filterProperty.value.uuid];
  }
  // return filterPropId ? twinEditCreateStore.mergedInputs[filterPropId] : null;
  return null;
});

function loadFilterData() {
  twinStore.loadValueListItems(listMetaData.value.filterId, false);
}

watch(isFiltered, (f) => {
  if (f) {
    loadFilterData();
  }
});

const filterValueListItem = computed(() =>
  (twinStore.valueListItemsLoadingState.get(filterId.value)?.result ?? []).find(
    (item) => item.value === filterValue.value,
  ),
);

const filteredResult = computed<ValueListItem[]>(() => {
  return filterValueListItem.value != null
    ? result.value.filter(
        (item) => item.filterValue === filterValueListItem.value.uuid,
      )
    : [];
});

watch(filterValue, (newVal, oldVal) => {
  if (oldVal !== null && oldVal !== newVal) {
    model.value = null;
  }
});

async function loadData() {
  if (id.value) {
    await twinStore.loadValueListItems(id.value, false);
  }
  if (isFiltered.value) {
    loadFilterData();
  }
}

const selectFilterFirst = computed(() => {
  return isFiltered.value && !filterValueListItem.value;
});

// Additional admin functionality
const authStore = useAuthStore();

const hasSmxModelMgmtRole = computed(() => {
  const modelmgmtRoles = authStore.rolesByClientId?.["smx-model-mgmt"];
  if (!modelmgmtRoles) {
    return false;
  }
  return modelmgmtRoles.roles.length > 0;
});

// Editing
const editInputValues = ref(false);
</script>
